import { parse } from "date-fns";
import { useEffect, useState } from "react";

import { RetriageEligibilityResponseProto } from "@noom/noom-contracts/noom_contracts/backend/partner/service/partner_eligibility_service";
import { Api } from "@noom/noomscape";

import { DOB_FORMAT } from "@/constants";
import { useAppContext } from "@/contexts";
import { RetriageEligibilityVerificationProperties } from "@/models";
import { getAutologinData } from "@/utils/cookies";
import {
  getProgramFromOffering,
  getEligibleProgramsFromEligibleOfferings,
} from "@/utils/programs";
import { createCivilDate } from "@/utils/proto";
import { captureException } from "@/utils/sentry";

import { useGoto } from "./useGoto";
import { useTrackEvent } from "./useTrackEvent";

export const useRetriageEligibility = (partnerId?: number) => {
  const { retriageEligibility, setRetriageEligibility, setQualifiedPrograms } =
    useAppContext();
  const goto = useGoto();
  const [isLoadingRetriageEligibility, setIsLoadingRetriageEligibility] =
    useState(false);
  const [checkingAutologinData, setCheckingAutologinData] = useState(false);
  const [verificationProperties, setVerificationProperties] =
    useState<RetriageEligibilityVerificationProperties>();
  const { linkB2BIdentity, trackAnonEvent, trackIdentifiedEvent } =
    useTrackEvent();

  const resetRetriageEligibility = () => {
    setRetriageEligibility(undefined);
    setVerificationProperties(undefined);
  };

  const handleRetriageEligibilityResponse = (
    response: RetriageEligibilityResponseProto,
    didVerifyUsingAutologinData = false,
  ) => {
    const {
      accessCode,
      currentOffering,
      eligibilityId,
      eligibilityReason,
      eligibleRetriageOfferings,
      userProfile,
      partnerSlug,
      segmentId,
      segmentName,
    } = response;

    const isEligible = eligibilityReason === "ELIGIBLE";
    const currentProgram = getProgramFromOffering(currentOffering);

    // The logic that transforms offerings into programs includes Weight
    // because of how Med and Diabetes coursepacks are structured. For
    // enrollment it makes sense to include Weight, but for retriage, Weight
    // is never an eligible program, so we're only filtering it out for
    // retriage.
    const eligibleRetriagePrograms = getEligibleProgramsFromEligibleOfferings(
      eligibleRetriageOfferings,
    ).filter((program) => program !== "WEIGHT");
    setQualifiedPrograms(eligibleRetriagePrograms);

    trackAnonEvent("b2bEnrollmentAnonEligibilityChecked", {
      eligibleForEnrollment: isEligible,
      eligibilityStatus: eligibilityReason,
      currentProgram: currentProgram || "PROGRAM_UNSPECIFIED",
    });

    trackIdentifiedEvent("b2bEnrollmentIdentifiedEligibilityChecked", {
      eligibleForEnrollment: isEligible,
      eligibilityStatus: eligibilityReason,
    });

    setRetriageEligibility({
      accessCode,
      currentProgram,
      didVerifyUsingAutologinData,
      eligibilityId,
      eligibilityReason,
      eligibleRetriagePrograms,
      userProfile,
      partnerSlug,
      segmentId,
      segmentName,
    });

    if (accessCode) {
      linkB2BIdentity(accessCode);
    }
  };

  useEffect(() => {
    if (retriageEligibility || !partnerId || !verificationProperties) {
      return () => undefined;
    }

    let ignore = false;
    async function fetch() {
      if (!verificationProperties) {
        return;
      }

      setIsLoadingRetriageEligibility(true);

      try {
        const { dateOfBirth, ...rest } = verificationProperties;

        const response: RetriageEligibilityResponseProto = await Api.call(
          "partner.retriageEligibilityByEligibilityInfo",
          Api.api.partner.retriageEligibilityByEligibilityInfo,
          {
            partnerId,
            ...rest,
            ...(dateOfBirth && {
              dateOfBirth: createCivilDate(
                parse(dateOfBirth, DOB_FORMAT, new Date()),
              ),
            }),
          },
        );

        if (ignore) {
          return;
        }

        handleRetriageEligibilityResponse(response);
      } catch (e) {
        if (ignore) {
          return;
        }

        goto.error();
      }
      setIsLoadingRetriageEligibility(false);
    }
    fetch();
    return () => {
      ignore = true;
    };
  }, [verificationProperties, partnerId, retriageEligibility]);

  useEffect(() => {
    const autologinData = getAutologinData();
    if (!autologinData) {
      return () => undefined;
    }

    const { accessCode, accessToken } = autologinData;

    if (retriageEligibility && retriageEligibility.accessCode === accessCode) {
      // already fetched the user's eligibility on a previous page
      return () => undefined;
    }

    let ignore = false;
    async function fetch() {
      setCheckingAutologinData(true);
      try {
        const response: RetriageEligibilityResponseProto = await Api.call(
          "partner.retriageEligibilityForAccessCode",
          Api.api.partner.retriageEligibilityForAccessCode,
          {
            accessCode,
            accessToken,
          },
        );

        if (ignore) {
          return;
        }

        handleRetriageEligibilityResponse(response, true);
      } catch (e) {
        if (ignore) {
          return;
        }
        captureException(e);
      }
      setCheckingAutologinData(false);
    }
    fetch();
    return () => {
      ignore = true;
    };
  }, []);

  return {
    verificationProperties,
    setVerificationProperties,
    resetRetriageEligibility,
    checkingAutologinData,
    isLoadingRetriageEligibility,
  };
};
